import { jsx as _jsx } from "react/jsx-runtime";
import { Elements, ExpressCheckoutElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { PaymentsResource } from '../../core/resources/payments';
import { useExpressPaymentMethods } from '../hooks/useExpressPaymentMethods';
import { usePaymentPolling } from '../hooks/usePaymentPolling';
import { useStepConfig } from '../hooks/useStepConfig';
import { getGlobalApiClient } from '../hooks/useApiQuery';
// Express method keys — drives processorGroup detection and ECE paymentMethods options.
// 'klarna_express' is the CRM config key for Klarna via ECE, distinct from 'klarna' (redirect flow).
// Backward compatible: existing configs with only apple_pay/google_pay continue to match.
const EXPRESS_METHOD_KEYS = ['apple_pay', 'google_pay', 'link', 'klarna_express'];
// Inner component — must be a child of <Elements> to use useStripe() and useElements()
function StripeExpressButtonInner({ checkout, processorId, enabledExpressMethods, onSuccess, onError, onCancel, }) {
    const stripe = useStripe();
    const elements = useElements();
    const { handleAddExpressId, updateCheckoutSessionValues, updateCustomerEmail } = useExpressPaymentMethods();
    const { startPolling } = usePaymentPolling();
    const isProcessingRef = useRef(false);
    const [visible, setVisible] = useState(false);
    // Per-step country allow-list (CRM-injected stepConfig). Empty/missing = all allowed.
    const { stepConfig } = useStepConfig();
    const countryAllowlist = useMemo(() => {
        const list = stepConfig?.addressSettings?.countryAllowlist;
        if (!list || list.length === 0)
            return undefined;
        return list.map((c) => c.toUpperCase());
    }, [stepConfig]);
    const paymentsResource = useMemo(() => new PaymentsResource(getGlobalApiClient()), []);
    // Push amount/currency changes (e.g. shipping cost added once address resolved,
    // promo applied, quantity changed) to the mounted ExpressCheckoutElement.
    // Re-rendering <Elements> with new options does not update the underlying
    // element — Stripe requires elements.update() for live updates.
    const liveAmount = Math.round(checkout.summary?.totalAdjustedAmount ?? 0);
    const liveCurrency = (checkout.summary?.currency || 'usd').toLowerCase();
    useEffect(() => {
        if (!elements)
            return;
        elements.update({ amount: liveAmount, currency: liveCurrency });
    }, [elements, liveAmount, liveCurrency]);
    const onConfirm = async (event) => {
        if (!stripe || !elements || isProcessingRef.current)
            return;
        isProcessingRef.current = true;
        // Use the wallet type directly from Stripe's event — 'apple_pay', 'google_pay', 'paypal', etc.
        const paymentMethod = event.expressPaymentType;
        try {
            // Step 1: save billing address + email from the wallet to the session/customer record.
            // This mirrors what ApplePayButton and GooglePayButton do before processing payment,
            // and ensures customer.email is present for the backend email validation.
            const billing = event.billingDetails;
            const eventShipping = event.shippingAddress;
            if (billing) {
                const nameParts = (billing.name ?? '').trim().split(/\s+/);
                const firstName = nameParts[0] ?? '';
                const lastName = nameParts.slice(1).join(' ');
                const billingAddress = {
                    firstName,
                    lastName,
                    address1: billing.address?.line1 ?? '',
                    address2: billing.address?.line2 ?? '',
                    city: billing.address?.city ?? '',
                    state: billing.address?.state ?? '',
                    country: billing.address?.country ?? '',
                    postal: billing.address?.postal_code ?? '',
                    phone: billing.phone ?? '',
                    email: billing.email ?? '',
                };
                // When shippingAddressRequired is true, Stripe provides event.shippingAddress.
                // Otherwise the billing address is used as the shipping address (legacy behavior).
                const shippingFromEvent = eventShipping?.address
                    ? {
                        firstName: (eventShipping.name ?? '').trim().split(/\s+/)[0] ?? '',
                        lastName: (eventShipping.name ?? '').trim().split(/\s+/).slice(1).join(' '),
                        address1: eventShipping.address.line1 ?? '',
                        address2: eventShipping.address.line2 ?? '',
                        city: eventShipping.address.city ?? '',
                        state: eventShipping.address.state ?? '',
                        country: eventShipping.address.country ?? '',
                        postal: eventShipping.address.postal_code ?? '',
                        phone: billing.phone ?? '',
                        email: billing.email ?? '',
                    }
                    : billingAddress;
                // Defense-in-depth: reject if wallet-returned country isn't in allowlist
                const shippingCountry = shippingFromEvent.country.toUpperCase();
                if (countryAllowlist && shippingCountry && !countryAllowlist.includes(shippingCountry)) {
                    console.error('[StripeExpress] Shipping country not in allowlist:', shippingCountry);
                    onError?.('Shipping to this country is not supported');
                    isProcessingRef.current = false;
                    return;
                }
                await updateCheckoutSessionValues({
                    data: {
                        shippingAddress: shippingFromEvent,
                        billingAddress,
                    },
                });
            }
            if (billing?.email) {
                await updateCustomerEmail({ data: { email: billing.email } });
            }
            // Step 2: validate the Elements form
            const { error: submitError } = await elements.submit();
            if (submitError) {
                onError?.(submitError.message ?? 'Validation failed');
                isProcessingRef.current = false;
                return;
            }
            // Step 3: call backend via existing APM path
            // paymentsResource.processPaymentDirect returns the raw PaymentResponse (no action routing)
            // Forward the session's selected shipping rate so the order is created
            // with shipping even if the session lookup is racing with this request.
            const shippingRateId = checkout.checkoutSession.shippingRateId
                || checkout.checkoutSession.shippingRate?.id;
            const response = await paymentsResource.processPaymentDirect(checkout.checkoutSession.id, '', // empty — backend identifies via processorId + paymentMethod
            undefined, {
                processorId,
                paymentMethod,
                isExpress: true,
                shippingRateId,
            });
            const clientSecret = response?.payment?.requireActionData?.metadata?.stripeExpressCheckout?.clientSecret;
            if (!clientSecret) {
                onError?.('Express checkout configuration missing — no client secret returned');
                isProcessingRef.current = false;
                return;
            }
            // Step 4: confirm the PaymentIntent inline — must happen while the Apple Pay sheet is open
            const { error: confirmError } = await stripe.confirmPayment({
                elements,
                clientSecret,
                confirmParams: {
                    // redirect: 'if_required' avoids a full-page redirect for wallet payments
                    return_url: window.location.href,
                },
                redirect: 'if_required',
            });
            if (confirmError) {
                onError?.(confirmError.message ?? 'Payment confirmation failed');
                isProcessingRef.current = false;
                return;
            }
            // Step 5: poll for the final status — the webhook (payment_intent.succeeded) updates the DB
            const paymentId = response.payment.id;
            startPolling(paymentId, {
                onRequireAction: (payment, stop) => {
                    // The payment still has requireAction='stripe_express_checkout' from when we created
                    // the intent — this has already been handled inline by stripe.confirmPayment.
                    // Ignore it and let polling continue waiting for the webhook to mark it succeeded.
                    if (payment.requireActionData?.type === 'stripe_express_checkout')
                        return;
                    // Any other unexpected action type: stop and surface as error
                    stop();
                    isProcessingRef.current = false;
                    onError?.('Unexpected payment action required');
                },
                onSuccess: async (completedPayment) => {
                    isProcessingRef.current = false;
                    onSuccess?.({ payment: completedPayment, order: completedPayment.order });
                },
                onFailure: async (errorMsg) => {
                    isProcessingRef.current = false;
                    onError?.(errorMsg);
                },
            });
        }
        catch (err) {
            isProcessingRef.current = false;
            const msg = err instanceof Error ? err.message : 'Express checkout failed';
            console.error('[StripeExpressButton] Payment error:', err);
            onError?.(msg);
        }
    };
    // When ECE has no available wallets, keep the element mounted (so onReady can still
    // fire if the browser later exposes one) but pull it off-screen so it doesn't reserve
    // an empty slot. Off-screen rather than zero-size so the iframe still gets layout.
    const hiddenStyle = {
        position: 'absolute',
        left: '-9999px',
        top: '-9999px',
        pointerEvents: 'none',
    };
    return (_jsx("div", { style: visible ? undefined : hiddenStyle, children: _jsx(ExpressCheckoutElement, { onReady: ({ availablePaymentMethods }) => {
                if (availablePaymentMethods) {
                    setVisible(true);
                    handleAddExpressId('stripe_express_checkout');
                }
            }, onConfirm: onConfirm, onCancel: () => {
                isProcessingRef.current = false;
                onCancel?.();
            }, options: {
                buttonType: {
                    applePay: 'buy',
                    googlePay: 'buy',
                },
                buttonHeight: 48,
                paymentMethods: {
                    applePay: enabledExpressMethods.includes('apple_pay') ? 'always' : 'never',
                    googlePay: enabledExpressMethods.includes('google_pay') ? 'always' : 'never',
                    link: enabledExpressMethods.includes('link') ? 'auto' : 'never',
                    klarna: enabledExpressMethods.includes('klarna_express') ? 'auto' : 'never',
                },
                emailRequired: true,
                billingAddressRequired: true,
                // When an allow-list is configured, collect shipping via the wallet sheet so Stripe
                // can enforce country restrictions natively. allowedShippingCountries requires
                // shippingAddressRequired: true.
                ...(countryAllowlist
                    ? {
                        shippingAddressRequired: true,
                        allowedShippingCountries: countryAllowlist,
                    }
                    : {}),
            } }) }));
}
// Outer component — resolves Stripe instance and provides <Elements> context
export const StripeExpressButton = (props) => {
    const { stripeExpressPaymentMethod } = useExpressPaymentMethods();
    const processorGroup = stripeExpressPaymentMethod?.settings?.processors?.find((g) => EXPRESS_METHOD_KEYS.some((key) => g?.methods?.[key]?.enabled === true));
    const processorId = processorGroup?.processorId;
    const publishableKey = stripeExpressPaymentMethod?.settings?.publishableKey;
    // Derive which express methods are enabled in this processor group
    const enabledExpressMethods = EXPRESS_METHOD_KEYS.filter((key) => processorGroup?.methods?.[key]?.enabled === true);
    const stripePromise = useMemo(() => (publishableKey ? loadStripe(publishableKey) : null), [publishableKey]);
    if (!stripeExpressPaymentMethod || !processorId || !stripePromise)
        return null;
    const amount = Math.round(props.checkout.summary?.totalAdjustedAmount ?? 0);
    const currency = (props.checkout.summary?.currency || 'usd').toLowerCase();
    const elementsOptions = {
        mode: 'payment',
        amount,
        currency,
        // 'auto' lets Stripe Elements derive the locale from navigator.language;
        // an explicit code (e.g. 'fr', 'pt-BR') forces a specific translation
        // for PayPal / Klarna / Link sheets. Stripe filters unsupported codes
        // back to English internally, so it's safe to forward any string.
        locale: (props.locale ?? 'auto'),
    };
    return (_jsx(Elements, { stripe: stripePromise, options: elementsOptions, children: _jsx(StripeExpressButtonInner, { ...props, processorId: processorId, enabledExpressMethods: enabledExpressMethods }) }));
};
export default StripeExpressButton;
